import { useCallback, useEffect, useMemo, useState } from 'react';
import { AuthSession, LoginCredentials, LoginResponse } from '@/application/auth/authTypes';
import * as authApi from '@/infrastructure/http/authApi';
import { AuthContext, AuthContextValue } from './authContextCore';

const AUTH_STORAGE_KEY = 'panelclinicas_auth';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(() => readStoredSession());
  const [bootstrapping, setBootstrapping] = useState(Boolean(session?.token));

  const logout = useCallback(() => {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    setSession(null);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function bootstrapSession() {
      if (!session?.token) {
        setBootstrapping(false);
        return;
      }

      try {
        const user = await authApi.getCurrentUser(session.token);
        if (!cancelled) {
          const refreshed = { token: session.token, user };
          setSession(refreshed);
          localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(refreshed));
        }
      } catch {
        if (!cancelled) {
          logout();
        }
      } finally {
        if (!cancelled) {
          setBootstrapping(false);
        }
      }
    }

    bootstrapSession();

    return () => {
      cancelled = true;
    };
  }, [logout, session?.token]);

  const handleLogin = useCallback(async (credentials: LoginCredentials): Promise<LoginResponse> => {
    const result = await authApi.login(credentials);
    if (!result.token || !result.user) {
      return result;
    }
    const user = await authApi.getCurrentUser(result.token);
    const refreshed = { token: result.token, user };
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(refreshed));
    setSession(refreshed);
    return { ...result, token: result.token, user };
  }, []);

  const handleImpersonate = useCallback(async (username: string) => {
    const nextSession = await authApi.impersonate(username);
    const user = await authApi.getCurrentUser(nextSession.token);
    const refreshed = { token: nextSession.token, user };
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(refreshed));
    setSession(refreshed);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: session?.user ?? null,
      token: session?.token ?? null,
      bootstrapping,
      login: handleLogin,
      impersonate: handleImpersonate,
      logout,
    }),
    [bootstrapping, handleImpersonate, handleLogin, logout, session]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function readStoredSession(): AuthSession | null {
  const saved = localStorage.getItem(AUTH_STORAGE_KEY);
  if (!saved) {
    return null;
  }

  try {
    const parsed = JSON.parse(saved) as Partial<AuthSession>;
    if (!parsed.token || !parsed.user?.username) {
      return null;
    }

    return parsed as AuthSession;
  } catch {
    return null;
  }
}
